import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, Button, Input, Table} from 'antd';
import {GET_COMPLY_CUBE_DOCUMENTS} from '../resolvers';
import {
  buildDocumentsInput,
  formatCheckTypeLabel,
  formatDate,
  unwrapListResponse,
  COMPLY_CUBE_DOCUMENTS_KEY,
} from '../kycComplyCubeUtils';
import {
  ComplyCubeTablePagination,
  ComplyCubeTruncatedClientId,
  ComplyCubeUserCell,
} from './ComplyCubeTableShared';
import styles from '../kycDashboard.module.scss';

export function ComplyCubeDocumentsTab({openClientDetail}) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchDraft, setSearchDraft] = useState('');
  const [clientIdDraft, setClientIdDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedClientId, setAppliedClientId] = useState('');

  const showReset =
    searchDraft.trim() !== '' ||
    clientIdDraft.trim() !== '' ||
    appliedSearch.trim() !== '' ||
    appliedClientId.trim() !== '';

  const handleApply = () => {
    setAppliedSearch(searchDraft);
    setAppliedClientId(clientIdDraft.trim());
    setPage(1);
  };

  const handleReset = () => {
    setSearchDraft('');
    setClientIdDraft('');
    setAppliedSearch('');
    setAppliedClientId('');
    setPage(1);
  };

  const variables = useMemo(
    () => ({
      input: buildDocumentsInput(page, limit, {
        search: appliedSearch,
        clientId: appliedClientId,
        type: '',
        issuingCountry: '',
      }),
    }),
    [page, limit, appliedSearch, appliedClientId],
  );

  const {data, loading} = useQuery(GET_COMPLY_CUBE_DOCUMENTS, {
    variables,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const {error, rows, total} = unwrapListResponse(
    data,
    COMPLY_CUBE_DOCUMENTS_KEY,
    'ResponseWithComplyCubeDocuments',
  );

  return (
    <>
      {error ? (
        <Alert
          type="error"
          message={error.message || 'Request failed'}
          showIcon
        />
      ) : null}
      <div className={styles.viewerFilters}>
        <Input
          allowClear
          placeholder="Search…"
          className={styles.viewerSearch}
          value={searchDraft}
          onChange={e => setSearchDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <Input
          allowClear
          placeholder="Client ID"
          className={styles.viewerFilterInput}
          value={clientIdDraft}
          onChange={e => setClientIdDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <div className={styles.viewerFilterActions}>
          <Button type="primary" onClick={handleApply}>
            Submit
          </Button>
          {showReset ? <Button onClick={handleReset}>Reset</Button> : null}
        </div>
      </div>
      <Table
        className="antd-table"
        loading={loading}
        pagination={false}
        rowKey={r => r.id || r._id}
        dataSource={rows}
        onRow={record => ({
          onClick: () => {
            const cid = record?.clientId;
            if (cid) openClientDetail(cid);
          },
          className: styles.clickableRow,
        })}
        columns={[
          {
            title: 'User',
            key: 'user',
            width: 228,
            render: (_, r) => <ComplyCubeUserCell user={r?.user} />,
          },
          {
            title: 'ID',
            key: 'id',
            width: 168,
            render: (_, r) => (
              <ComplyCubeTruncatedClientId
                id={r?.id || r?._id}
                copiedMessage="Document ID copied"
                copyAriaLabel="Copy document ID"
              />
            ),
          },
          {
            title: 'Client ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 168,
            render: id => <ComplyCubeTruncatedClientId id={id} />,
          },
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: v => formatCheckTypeLabel(v),
          },
          {
            title: 'Issuing country',
            dataIndex: 'issuingCountry',
            key: 'issuingCountry',
          },
          {
            title: 'Classification',
            dataIndex: 'classification',
            key: 'classification',
            render: v => formatCheckTypeLabel(v),
          },
          {
            title: '# Images',
            key: 'nimg',
            render: (_, r) => (r?.images || []).length,
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: formatDate,
          },
        ]}
      />
      <ComplyCubeTablePagination
        page={page}
        limit={limit}
        total={total}
        rowCount={rows.length}
        loading={loading}
        onPageChange={(nextPage, nextLimit) => {
          setLimit(nextLimit);
          setPage(nextPage);
        }}
        itemName="documents"
      />
    </>
  );
}
